import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MemoryChapterCard } from '../MemoryChapterCard';
import { MemoryModal } from '../MemoryModal';
import { Button } from '../../Button';
import { logEvent } from '../../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../../lib/shared/event-types';
import styles from './MemoryChapters.css';

function MemoryChapters({ chapters, max, onSelectChapter }) {
    const [memoryChapter, setMemoryChapter] = useState(null);
    const [isBookComingSoonVisible, setIsBookComingSoonVisible] = useState(false);

    const handleSelect = (chapter) => {
        if (chapter.isMemoryUnlocked) {
            logEvent(EVENT_TYPES.MEMORY_KEEPER_MEMORY_VIEWED, { 'chapter_id': chapter.id });
            setMemoryChapter(chapter);

            return;
        }

        onSelectChapter(chapter.id);
    };

    const handleBookInterestClick = () => {
        logEvent(EVENT_TYPES.MEMORY_KEEPER_BOOK_INTEREST_CLICKED);
        setIsBookComingSoonVisible(true);
    };

    return (
        <section className={styles.root}>
            <h1 className={styles.title}>Your Memories</h1>
            <p className={styles.subtitle}>Each chapter needs {max} answers to create a memory.</p>
            <ul className={styles.grid}>
                {chapters.map((chapter) => (
                    <li key={chapter.id} className={styles.cell}>
                        <MemoryChapterCard
                            label={chapter.label}
                            count={chapter.answerCount}
                            max={chapter.answerCount + chapter.answersUntilMemory}
                            isReady={chapter.isMemoryUnlocked}
                            isPending={chapter.isMemoryPending}
                            onSelect={() => handleSelect(chapter)}
                        />
                    </li>
                ))}
            </ul>

            <div className={styles.card}>
                <div className={styles.cardIntro}>
                    <h2 className={styles.cardTitle}>Collect your chapters into a book</h2>
                    <p className={styles.cardText}>
                        Soon you&apos;ll be able to gather all your chapters into one keepsake book.
                    </p>
                </div>
                <Button
                    label={isBookComingSoonVisible ? 'Coming soon' : 'Notify me'}
                    type='primary'
                    iconLeft={{ name: isBookComingSoonVisible ? 'clock' : 'bell' }}
                    disabled={isBookComingSoonVisible}
                    buttonProps={{ type: 'button' }}
                    onClick={handleBookInterestClick}
                    className={styles.cardButton}
                />
            </div>

            <MemoryModal
                isOpen={Boolean(memoryChapter)}
                onCloseModal={() => setMemoryChapter(null)}
                chapter={memoryChapter?.id ?? ''}
            />
        </section>
    );
}

MemoryChapters.propTypes = {
    chapters: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        answerCount: PropTypes.number.isRequired,
        answersUntilMemory: PropTypes.number.isRequired,
        isMemoryUnlocked: PropTypes.bool.isRequired,
        isMemoryPending: PropTypes.bool.isRequired
    })).isRequired,
    max: PropTypes.number.isRequired,
    onSelectChapter: PropTypes.func.isRequired
};

export {
    MemoryChapters
};
